import { client } from 'f';
import { sendErrorToServices } from 'lib/logging';
import Script from 'next/script';
import { useEffect } from 'react';

const SCRIPT_ID = 'yourbow-prebid-script';
const SCRIPT_SRC = 'https://cdn.yourbow.com/ted/hbyb.js';

// Initialize prebid queue
function initializePrebid(): void {
  if (!window.pbjs) {
    window.pbjs = { que: [] };
  }
}

// Add a function to watch for and protect companion ads globally
function protectCompanionAds() {
  if (typeof window === 'undefined' || !window.googletag) return;

  // Set up a global guard against anyone refreshing ad slots
  window.googletag.cmd.push(() => {
    const pubads = window.googletag.pubads?.();
    if (!pubads || !pubads.refresh) return;

    // Store the original refresh function
    const originalRefresh = pubads.refresh;

    // Replace with our protected version
    pubads.refresh = function (slots) {
      // Get current talk ID from URL
      const currentTalkMatch =
        window.location.pathname.match(/\/talks\/([^\/\?]+)/);
      const currentTalkId = currentTalkMatch ? currentTalkMatch[1] : '';

      // Check for companions associated with current talk
      const companionElements = document.querySelectorAll(
        currentTalkId
          ? `[data-ad-companion="true"][data-talk-id="${currentTalkId}"]`
          : '[data-ad-companion="true"]'
      );
      // If companions exist, block refresh
      if (companionElements.length > 0) return;

      // If no slots provided, do normal refresh
      if (!slots) return originalRefresh.call(this, []);
      // Filter out protected slots
      const safeSlots = slots.filter(slot => {
        // Skip slots marked as protected
        if (slot.isProtectedCompanion) return false;

        // Check element attributes
        const slotId = slot.getSlotElementId?.();
        if (!slotId) return true;

        const element = document.getElementById(slotId);
        return !(
          element &&
          (element.hasAttribute('data-ad-companion') ||
            element.hasAttribute('data-dai-companion'))
        );
      });
      // Only refresh safe slots
      if (safeSlots.length > 0) {
        return originalRefresh.call(this, safeSlots);
      }
    };
  });
}

export function PrebidScript() {
  useEffect(() => {
    initializePrebid();
    // Disabled: Set up global protection for companion ads
    protectCompanionAds();
  }, []);

  return (
    <Script
      async
      id={SCRIPT_ID}
      src={SCRIPT_SRC}
      strategy="lazyOnload"
      onLoad={() => {
        window.yourBowScriptLoaded = true;
        window?.runAds?.();
      }}
      onError={e => {
        sendErrorToServices('Error loading Prebid script:', e);
      }}
    />
  );
}

export function refreshVideoAds(): void {
  if (!client) return;

  // Skip refresh if companions exist for current talk
  const currentTalkMatch =
    window.location.pathname.match(/\/talks\/([^\/\?]+)/);
  const currentTalkId = currentTalkMatch ? currentTalkMatch[1] : '';

  if (
    document.querySelectorAll(
      currentTalkId
        ? `[data-ad-companion="true"][data-talk-id="${currentTalkId}"]`
        : '[data-ad-companion="true"]'
    ).length > 0
  )
    return;

  window.pbjs?.que.push(() => window.runVideoAds?.());
}

// Helper to check if the script is loaded and ready
export function isPrebidReady(): boolean {
  return window.yourBowScriptLoaded === true && window.pbjs?.que !== undefined;
}
